import { Component, OnInit, OnDestroy, ViewChild, signal, inject, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';

import { TableModule, Table } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';

import { HttpClient } from '@angular/common/http';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, skip, firstValueFrom } from 'rxjs';
import { HeaderService } from '../../../services/header.service';
import { BlockchainResponseAdminDto } from '../../../api/models/blockchain-response-admin-dto';
import { CreateBlockchainDto } from '../../../api/models/create-blockchain-dto';
import { UpdateBlockchainDto } from '../../../api/models/update-blockchain-dto';
import {
  adminBlockchainsControllerListBlockchainsV1,
  adminBlockchainsControllerCreateBlockchainV1,
  adminBlockchainsControllerUpdateBlockchainV1,
  adminBlockchainsControllerDeleteBlockchainV1,
} from '../../../api/functions';
import { ApiConfiguration } from '../../../api/api-configuration';

@Component({
  selector: 'app-blockchains',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    IconFieldModule,
    InputIconModule,
    ConfirmDialogModule,
    MessageModule,
    TooltipModule,
    CheckboxModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './blockchains.component.html',
  styleUrl: './blockchains.component.css',
})
export class BlockchainsComponent implements OnInit, OnDestroy {
  @ViewChild('table') table!: Table;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly fb = inject(FormBuilder);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);

  protected readonly blockchains = signal<BlockchainResponseAdminDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly dialogVisible = signal(false);
  protected readonly dialogMode = signal<'create' | 'edit'>('create');
  protected readonly searchControl = new FormControl('');

  protected blockchainForm!: FormGroup;
  private editingBlockchain: BlockchainResponseAdminDto | null = null;
  private readonly destroy$ = new Subject<void>();
  private isSearching = false;

  ngOnInit(): void {
    this.headerService.setTitle(this.translateService.instant('blockchains.title'));
    this.headerService.setSubtitle(this.translateService.instant('blockchains.subtitle'));

    this.initForm();
    this.loadBlockchains();

    // Setup server-side search with debounce
    this.searchControl.valueChanges
      .pipe(skip(1), takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.isSearching = true;
        this.loadBlockchains(searchTerm || undefined);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.blockchainForm = this.fb.group({
      label: ['', Validators.required],
      description: [''],
      chainId: [null],
      isEVM: [true],
      rpcUrl: [''],
      explorerUrl: [''],
      explorerApiUrl: [''],
    });
  }

  protected loadBlockchains(search?: string): void {
    this.loading.set(true);
    this.searchControl.disable();

    firstValueFrom(
      adminBlockchainsControllerListBlockchainsV1(this.http, this.apiConfig.rootUrl, { search })
    )
      .then((response) => {
        this.blockchains.set(response?.body || []);
      })
      .catch((error) => {
        console.error('Error loading blockchains:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant('blockchains.loadError'),
        });
      })
      .finally(() => {
        this.loading.set(false);
        // Prevent triggering valueChanges while re-enabling control,
        // which could cause a second load request.
        this.searchControl.enable({ emitEvent: false });

        if (this.isSearching && this.searchInput) {
          setTimeout(() => {
            this.searchInput.nativeElement.focus();
            this.isSearching = false;
          }, 0);
        }
      });
  }

  protected openCreateDialog(): void {
    this.dialogMode.set('create');
    this.editingBlockchain = null;
    this.blockchainForm.reset();
    this.dialogVisible.set(true);
  }

  protected openEditDialog(blockchain: BlockchainResponseAdminDto): void {
    this.dialogMode.set('edit');
    this.editingBlockchain = blockchain;
    this.blockchainForm.patchValue({
      label: blockchain.label,
      description: blockchain.description || '',
      chainId: blockchain.chainId || null,
      isEVM: blockchain.isEVM ?? true,
      rpcUrl: blockchain.rpcUrl || '',
      explorerUrl: blockchain.explorerUrl || '',
      explorerApiUrl: blockchain.explorerApiUrl || '',
    });
    this.dialogVisible.set(true);
  }

  protected saveBlockchain(): void {
    if (this.blockchainForm.invalid) {
      Object.keys(this.blockchainForm.controls).forEach((key) => {
        this.blockchainForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading.set(true);

    if (this.dialogMode() === 'create') {
      this.createBlockchain();
    } else {
      this.updateBlockchain();
    }
  }

  private createBlockchain(): void {
    const body: CreateBlockchainDto = {
      label: this.blockchainForm.value.label,
      description: this.blockchainForm.value.description || undefined,
      chainId: this.blockchainForm.value.chainId || undefined,
      isEVM: this.blockchainForm.value.isEVM ?? true,
      rpcUrl: this.blockchainForm.value.rpcUrl || undefined,
      explorerUrl: this.blockchainForm.value.explorerUrl || undefined,
      explorerApiUrl: this.blockchainForm.value.explorerApiUrl || undefined,
    };

    firstValueFrom(
      adminBlockchainsControllerCreateBlockchainV1(this.http, this.apiConfig.rootUrl, { body })
    )
      .then(() => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('blockchains.createSuccess'),
        });
        this.dialogVisible.set(false);
        this.loadBlockchains();
      })
      .catch((error) => {
        console.error('Error creating blockchain:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: error.error?.message || this.translateService.instant('blockchains.createError'),
        });
        this.loading.set(false);
      });
  }

  private updateBlockchain(): void {
    if (!this.editingBlockchain) return;

    const body: UpdateBlockchainDto = {
      label: this.blockchainForm.value.label,
      description: this.blockchainForm.value.description || undefined,
      chainId: this.blockchainForm.value.chainId || undefined,
      isEVM: this.blockchainForm.value.isEVM ?? true,
      rpcUrl: this.blockchainForm.value.rpcUrl || undefined,
      explorerUrl: this.blockchainForm.value.explorerUrl || undefined,
      explorerApiUrl: this.blockchainForm.value.explorerApiUrl || undefined,
    };

    firstValueFrom(
      adminBlockchainsControllerUpdateBlockchainV1(this.http, this.apiConfig.rootUrl, {
        id: this.editingBlockchain.id,
        body,
      })
    )
      .then(() => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('blockchains.updateSuccess'),
        });
        this.dialogVisible.set(false);
        this.loadBlockchains();
      })
      .catch((error) => {
        console.error('Error updating blockchain:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: error.error?.message || this.translateService.instant('blockchains.updateError'),
        });
        this.loading.set(false);
      });
  }

  protected deleteBlockchain(blockchain: BlockchainResponseAdminDto): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('blockchains.deleteConfirmMessage', {
        name: blockchain.label,
      }),
      header: this.translateService.instant('blockchains.deleteConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.loading.set(true);

        firstValueFrom(
          adminBlockchainsControllerDeleteBlockchainV1(this.http, this.apiConfig.rootUrl, {
            id: blockchain.id,
          })
        )
          .then(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.success'),
              detail: this.translateService.instant('blockchains.deleteSuccess'),
            });
            // Optimistically remove from local list to avoid double refresh
            const current = this.blockchains();
            this.blockchains.set(current.filter((b) => b.id !== blockchain.id));
            this.loading.set(false);
          })
          .catch((error) => {
            console.error('Error deleting blockchain:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.error'),
              detail:
                error.error?.message || this.translateService.instant('blockchains.deleteError'),
            });
            this.loading.set(false);
          });
      },
    });
  }

  protected getDialogTitle(): string {
    return this.dialogMode() === 'create'
      ? this.translateService.instant('blockchains.createTitle')
      : this.translateService.instant('blockchains.editTitle');
  }

  protected isFieldInvalid(fieldName: string): boolean {
    const field = this.blockchainForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  protected getFieldError(fieldName: string): string {
    const field = this.blockchainForm.get(fieldName);
    if (!field || !field.errors || !field.touched) return '';

    if (field.errors['required']) {
      return this.translateService.instant('validation.required');
    }
    return '';
  }
}
